export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type UnauthorizedHandler = () => void;

export interface DownloadedFile {
  blob: Blob;
  fileName: string | null;
}

export class HttpClient {
  private csrfToken: string | null = null;
  private unauthorizedHandler: UnauthorizedHandler | null = null;

  constructor(private readonly baseUrl: string = '') {}

  setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
    this.unauthorizedHandler = handler;
  }

  async refreshCsrf(): Promise<void> {
    const response = await fetch(this.baseUrl + '/api/auth/csrf', {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new ApiError(response.status, `Request failed with HTTP ${response.status}.`);
    }
    this.csrfToken = this.readCookie('XSRF-TOKEN');
    if (!this.csrfToken) {
      throw new ApiError(0, 'The security token could not be read.');
    }
  }

  clearCsrf() {
    this.csrfToken = null;
  }

  private readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const prefix = `${encodeURIComponent(name)}=`;
    const entry = document.cookie.split('; ').find((value) => value.startsWith(prefix));
    return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
  }

  private async parseErrorBody(res: Response): Promise<string> {
    try {
      const body = (await res.json()) as {
        error?: string;
        errors?: string[] | Record<string, string[]>;
        invalidKeys?: string[];
        title?: string;
      };
      if (Array.isArray(body.errors) && body.errors.length) return body.errors.join('\n');
      if (body.errors && !Array.isArray(body.errors)) {
        const messages = Object.values(body.errors).flat();
        if (messages.length) return messages.join('\n');
      }
      if (body.error) {
        return Array.isArray(body.invalidKeys) && body.invalidKeys.length
          ? `${body.error} Invalid keys: ${body.invalidKeys.join(', ')}.`
          : body.error;
      }
      if (body.title) return body.title;
    } catch {
      // Fall through to the status message.
    }
    return `Request failed with HTTP ${res.status}.`;
  }

  private async handle<T>(res: Response): Promise<T> {
    if (!res.ok) {
      if (res.status === 401) this.unauthorizedHandler?.();
      const message = await this.parseErrorBody(res);
      throw new ApiError(res.status, message);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text.trim()) return undefined as T;
    return JSON.parse(text) as T;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    const response = await this.send(method, path, body, init);
    return this.handle<T>(response);
  }

  private async send(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<Response> {
    const headers = new Headers(init?.headers);
    let requestBody: BodyInit | undefined;
    if (body instanceof FormData) {
      requestBody = body;
    } else if (body !== undefined) {
      headers.set('Content-Type', 'application/json');
      requestBody = JSON.stringify(body);
    }

    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      if (!this.csrfToken) await this.refreshCsrf();
      headers.set('X-XSRF-TOKEN', this.csrfToken!);
    }

    return fetch(this.baseUrl + path, {
      ...init,
      method,
      body: requestBody,
      headers,
      credentials: 'include',
    });
  }

  get<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>('GET', path, undefined, init);
  }

  async getFile(path: string, init?: RequestInit): Promise<DownloadedFile> {
    const response = await this.send('GET', path, undefined, init);
    if (!response.ok) return this.handle<DownloadedFile>(response);
    return {
      blob: await response.blob(),
      fileName: contentDispositionFileName(response.headers.get('Content-Disposition')),
    };
  }

  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>('POST', path, body, init);
  }

  put<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>('PUT', path, body, init);
  }

  patch<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>('PATCH', path, body, init);
  }

  delete<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>('DELETE', path, undefined, init);
  }
}

export function contentDispositionFileName(header: string | null): string | null {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Fall back to the basic filename parameter.
    }
  }
  const basic = /filename="([^"]+)"|filename=([^;]+)/i.exec(header);
  return (basic?.[1] ?? basic?.[2])?.trim() ?? null;
}

export const api = new HttpClient();
